import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { messagesAPI, clientsAPI, projectsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Plus,
  Building2,
  FolderOpen,
  Loader2,
  Inbox,
} from 'lucide-react';
import { formatRelativeTime } from '../../lib/utils';

const MessagesPage = () => {
  const { user, isStaff, isClient } = useAuth();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newConvoDialogOpen, setNewConvoDialogOpen] = useState(false);
  const [newConvoData, setNewConvoData] = useState({ client_id: '', project_id: '', contenu: '' });

  // Fetch clients list (for staff) or user's client (for client users)
  const fetchInitialData = useCallback(async () => {
    try {
      if (isStaff) {
        const [clientsRes, projectsRes] = await Promise.all([
          clientsAPI.getAll(),
          projectsAPI.getAll(),
        ]);
        setClients(clientsRes.data);
        setProjects(projectsRes.data);
        
        // Build conversations list from all clients
        const allConversations = [];
        for (const client of clientsRes.data.slice(0, 20)) { // Limit for performance
          try {
            const msgRes = await messagesAPI.getByClient(client.id);
            if (msgRes.data.length > 0) {
              allConversations.push({
                client,
                lastMessage: msgRes.data[0],
                messageCount: msgRes.data.length,
              });
            }
          } catch (e) {
            // Skip if error
          }
        }
        setConversations(allConversations.sort((a, b) => 
          new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
        ));
      } else if (isClient && user?.client_id) {
        // Client user - fetch their messages directly
        const clientRes = await clientsAPI.getById(user.client_id);
        setSelectedClient(clientRes.data);
        const msgRes = await messagesAPI.getByClient(user.client_id);
        setMessages(msgRes.data);
        const projRes = await projectsAPI.getAll();
        setProjects(projRes.data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  }, [isStaff, isClient, user]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleSelectConversation = async (client) => {
    setSelectedClient(client);
    try {
      const msgRes = await messagesAPI.getByClient(client.id);
      setMessages(msgRes.data);
      const projRes = await clientsAPI.getProjects(client.id);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedClient) return;
    setSendingMessage(true);
    try {
      await messagesAPI.send({
        client_id: selectedClient.id,
        contenu: newMessage,
      });
      toast.success('Message envoyé');
      setNewMessage('');
      // Refresh messages
      const msgRes = await messagesAPI.getByClient(selectedClient.id);
      setMessages(msgRes.data);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleNewConversation = async () => {
    if (!newConvoData.client_id || !newConvoData.contenu.trim()) return;
    setSendingMessage(true);
    try {
      await messagesAPI.send({
        client_id: newConvoData.client_id,
        project_id: newConvoData.project_id === 'none' ? null : (newConvoData.project_id || null),
        contenu: newConvoData.contenu,
      });
      toast.success('Message envoyé');
      setNewConvoDialogOpen(false);
      setNewConvoData({ client_id: '', project_id: '', contenu: '' });
      
      // Select the new conversation
      const client = clients.find(c => c.id === newConvoData.client_id);
      if (client) {
        handleSelectConversation(client);
      }
      fetchInitialData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Client view - direct messaging
  if (isClient) {
    return (
      <div className="space-y-6" data-testid="messages-page-client">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Messages</h1>
          <p className="text-slate-600">Communiquez avec PEY Consultants.</p>
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Message Input */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <Label className="mb-2 block">Nouveau message</Label>
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Écrivez votre message..."
                rows={3}
                className="mb-3"
                data-testid="message-input"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer
                </Button>
              </div>
            </div>

            {/* Messages */}
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Aucun message. Commencez la conversation!</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {messages.map((msg) => {
                  const isOwnMessage = msg.sender_user_id === user?.id;
                  const isStaffMessage = msg.sender_role !== 'client';
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] p-4 rounded-lg ${
                          isOwnMessage
                            ? 'bg-primary text-white'
                            : 'bg-blue-50 border border-blue-100'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium ${isOwnMessage ? 'text-white/90' : 'text-slate-700'}`}>
                            {isOwnMessage ? 'Vous' : msg.sender_nom}
                          </span>
                          {isStaffMessage && !isOwnMessage && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                              PEY
                            </Badge>
                          )}
                        </div>
                        <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-slate-600'}`}>
                          {msg.contenu}
                        </p>
                        <p className={`text-xs mt-2 ${isOwnMessage ? 'text-white/70' : 'text-slate-400'}`}>
                          {formatRelativeTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Staff view - conversations list + chat
  return (
    <div className="space-y-6" data-testid="messages-page-staff">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Messagerie</h1>
          <p className="text-slate-600">Communication avec les clients.</p>
        </div>
        <Dialog open={newConvoDialogOpen} onOpenChange={setNewConvoDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-conversation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau message
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Envoyer un message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select
                  value={newConvoData.client_id}
                  onValueChange={(v) => setNewConvoData({ ...newConvoData, client_id: v })}
                >
                  <SelectTrigger data-testid="select-client">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nom_entreprise}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Projet (optionnel)</Label>
                <Select
                  value={newConvoData.project_id}
                  onValueChange={(v) => setNewConvoData({ ...newConvoData, project_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Lier à un projet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {projects.filter(p => p.client_id === newConvoData.client_id).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  value={newConvoData.contenu}
                  onChange={(e) => setNewConvoData({ ...newConvoData, contenu: e.target.value })}
                  rows={4}
                  placeholder="Votre message..."
                  data-testid="new-message-content"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setNewConvoDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={handleNewConversation}
                  disabled={!newConvoData.client_id || !newConvoData.contenu.trim() || sendingMessage}
                  data-testid="submit-new-message"
                >
                  {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Conversations</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {conversations.length === 0 ? (
              <div className="p-6 text-center">
                <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Aucune conversation.</p>
              </div>
            ) : (
              <div className="divide-y">
                {conversations.map((conv) => (
                  <button
                    key={conv.client.id}
                    onClick={() => handleSelectConversation(conv.client)}
                    className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                      selectedClient?.id === conv.client.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                    }`}
                    data-testid={`conversation-${conv.client.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{conv.client.nom_entreprise}</p>
                        <p className="text-sm text-slate-500 truncate">{conv.lastMessage.contenu}</p>
                        <p className="text-xs text-slate-400">{formatRelativeTime(conv.lastMessage.created_at)}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {conv.messageCount}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            {!selectedClient ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Sélectionnez une conversation ou créez-en une nouvelle.</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 pb-4 border-b mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <Link
                      to={`/portail/clients/${selectedClient.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {selectedClient.nom_entreprise}
                    </Link>
                    <p className="text-sm text-slate-500">{selectedClient.personne_contact}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-4 max-h-[400px] overflow-y-auto mb-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Aucun message dans cette conversation.</p>
                  ) : (
                    messages.map((msg) => {
                      const isOwnMessage = msg.sender_user_id === user?.id;
                      const isStaffMessage = msg.sender_role !== 'client';
                      
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] p-4 rounded-lg ${
                              isOwnMessage
                                ? 'bg-primary text-white'
                                : 'bg-slate-100'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-sm font-medium ${isOwnMessage ? 'text-white/90' : 'text-slate-700'}`}>
                                {msg.sender_nom}
                              </span>
                            </div>
                            <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-slate-600'}`}>
                              {msg.contenu}
                            </p>
                            {msg.project_nom && (
                              <p className={`text-xs mt-2 ${isOwnMessage ? 'text-white/70' : 'text-slate-400'}`}>
                                <FolderOpen className="w-3 h-3 inline mr-1" />
                                {msg.project_nom}
                              </p>
                            )}
                            <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-slate-400'}`}>
                              {formatRelativeTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Input */}
                <div className="flex gap-3">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Votre message..."
                    rows={2}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="self-end"
                    data-testid="chat-send-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;
